import { formatISOTimeForDisplay, generateTimeOptions } from '@plato/shared-util/time-utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectList,
    SelectTrigger,
    SelectValue,
    type SelectOption,
} from '@plato/ui/components/select';
import { cn } from '@plato/ui/util/classes';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button.tsx';

export type TimePickerProps = {
    /**
     * The current value as a time string in HH:mm:ss format
     */
    value: string | null;
    /**
     * Callback when value changes, providing the new time string in HH:mm:ss format
     */
    onChange?: (value: string) => void;
    /**
     * Placeholder for the select input
     */
    placeholder?: string;
    /**
     * Whether the component is disabled
     */
    disabled?: boolean;
    /**
     * Additional class name for styling
     */
    className?: string;
    /**
     * The minimum time to display
     */
    min?: string;
    /**
     * The maximum time to display
     */
    max?: string;
    /**
     * Minutes between two adjacent options. Pass the same value a consumer does its own time
     * arithmetic with, so its bounds land on the grid.
     */
    stepMinutes?: number;
};

const PREFERRED_TIME_OPTION = '08:00:00';

/**
 * TimePicker component for selecting time values
 */
export function TimePicker({
    value,
    onChange,
    placeholder,
    disabled = false,
    className,
    min,
    max,
    stepMinutes = 15,
}: TimePickerProps) {
    const { t } = useTranslation('common');
    // The row the surface opens on: the picked time, or 08:00 while there is none.
    const initialFocusRef = React.useRef<HTMLButtonElement>(null);
    const times: SelectOption[] = React.useMemo(
        () =>
            generateTimeOptions(stepMinutes, min, max).map((time) => ({
                value: time,
                label: t('time_picker__time_option', { time: formatISOTimeForDisplay(time, 'HH:mm') }),
            })),
        [stepMinutes, min, max, t],
    );
    const focusedTime = value ?? PREFERRED_TIME_OPTION;

    return (
        <Select
            title={placeholder ?? t('time_picker__placeholder')}
            items={times}
            value={value ?? undefined}
            onValueChange={(time) => {
                if (onChange && time) {
                    onChange(time);
                }
            }}
        >
            <SelectTrigger
                disabled={disabled}
                render={<Button variant="ghost" className={cn('shrink w-auto', className)} />}
            >
                <SelectValue placeholder={placeholder ?? t('time_picker__placeholder')} />
            </SelectTrigger>
            <SelectContent initialFocus={initialFocusRef}>
                <SelectList>
                    {(time: SelectOption) => (
                        <SelectItem
                            key={time.value}
                            value={time}
                            ref={time.value === focusedTime ? initialFocusRef : undefined}
                        />
                    )}
                </SelectList>
            </SelectContent>
        </Select>
    );
}

export default TimePicker;
